/**
 * Card Quick Add (size bar)
 *
 * Works with snippets/dynamic-card-product.liquid:
 *  - `[data-card-size-data]`  JSON: sizes + variant ids for the card's product and its color siblings.
 *  - `[data-card-size-bar]`   container this script fills with one button per size.
 *  - `.card-quick-add__open`  "+" button handled by <modal-opener>/<quick-add-modal> (assets/quick-add.js).
 *
 * Behavior:
 *  - The bar shows the sizes of the color currently displayed; it is rebuilt on `card:colorchange`
 *    (dispatched by Dynamic-product-card.js when a swatch is hovered/clicked).
 *  - Click a size       -> POST /cart/add.js, then open the cart drawer.
 *  - Product with 2+ size options (Waist + Inseam) -> a single button that opens Quick View.
 *  - Sold-out sizes are disabled; if every size is sold out the bar stays hidden.
 *  - Many sizes (footwear) stay on one row; prev/next arrows scroll the row.
 */
(() => {
  if (window.CardQuickAdd) return;

  const SELECTOR = {
    card: '.product-card-wrapper',
    bar: '[data-card-size-bar]',
    data: '[data-card-size-data]',
    opener: '.card-quick-add__open',
  };
  const MESSAGE_TIMEOUT_MS = 4000;
  const SCROLL_STEP = 0.8;

  // Same arrow as the theme sliders (assets/icon-arrow-button.svg); the prev button rotates it in CSS.
  const ARROW_ICON =
    '<svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M8.36327 1.2832L15.635 8.28321M15.635 8.28321L8.36327 15.2832M15.635 8.28321L0.36328 8.28321" stroke="currentColor"/>' +
    '</svg>';

  // "+" of the "+ Quick view" button (20x20, 1.5 stroke).
  const PLUS_ICON =
    '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M4.16602 10H15.8327" stroke="#050505" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M10 4.1665V15.8332" stroke="#050505" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  // card element -> { items, current }
  const cardState = new WeakMap();

  function getState(card) {
    if (cardState.has(card)) return cardState.get(card);

    const script = card.querySelector(SELECTOR.data);
    if (!script) return null;

    try {
      const state = JSON.parse(script.textContent);
      cardState.set(card, state);
      return state;
    } catch (error) {
      console.error('card-quick-add: invalid size data', error);
      return null;
    }
  }

  function activeProductId(card, state) {
    const active = card.querySelector('.product-variant-color-picker-link.active[data-product-id]');
    return active ? Number(active.dataset.productId) : state.current;
  }

  // --- Rendering --------------------------------------------------------------

  function createElement(tag, className, attributes = {}) {
    const el = document.createElement(tag);
    if (className) el.className = className;
    Object.entries(attributes).forEach(([key, value]) => el.setAttribute(key, value));
    return el;
  }

  function updateArrows(bar) {
    const list = bar.querySelector('.card-size-bar__list');
    if (!list) return;
    const overflow = list.scrollWidth > list.clientWidth + 1;
    const prev = bar.querySelector('.card-size-bar__arrow--prev');
    const next = bar.querySelector('.card-size-bar__arrow--next');
    if (!prev || !next) return;
    prev.hidden = !overflow || list.scrollLeft <= 0;
    next.hidden = !overflow || list.scrollLeft + list.clientWidth >= list.scrollWidth - 1;
  }

  function render(card, productId) {
    const bar = card.querySelector(SELECTOR.bar);
    const state = getState(card);
    if (!bar || !state) return;

    const item = state.items.find((entry) => entry.id === productId) || state.items[0];
    bar.textContent = '';
    bar.hidden = true;
    if (!item) return;

    bar.dataset.productId = item.id;
    bar.dataset.productTitle = item.title;

    // Products with 2+ size options (Waist + Inseam): one click cannot pick both,
    // so the bar is a single "+ Quick view" button that opens the popup.
    bar.classList.toggle('card-size-bar--quick-view', item.multi);
    if (item.multi) {
      const button = createElement('button', 'card-size-bar__choose', { type: 'button' });
      button.innerHTML = PLUS_ICON;
      button.append(bar.dataset.quickViewLabel || 'Quick view');
      bar.appendChild(button);
      bar.hidden = false;
      return;
    }

    const hasAvailable = item.sizes.some((size) => size.a);
    if (!item.sizes.length || !hasAvailable) return;

    const list = createElement('ul', 'card-size-bar__list', { role: 'list' });
    item.sizes.forEach((size) => {
      const li = createElement('li', 'card-size-bar__item');
      const button = createElement('button', 'card-size-bar__size', {
        type: 'button',
        'data-variant-id': size.id,
        'aria-label': `${item.title} ${size.t}`,
      });
      button.textContent = size.t;
      if (!size.a) {
        button.disabled = true;
        button.classList.add('is-sold-out');
      }
      li.appendChild(button);
      list.appendChild(li);
    });

    const prev = createElement('button', 'card-size-bar__arrow card-size-bar__arrow--prev', {
      type: 'button',
      'aria-hidden': 'true',
      tabindex: '-1',
    });
    const next = createElement('button', 'card-size-bar__arrow card-size-bar__arrow--next', {
      type: 'button',
      'aria-hidden': 'true',
      tabindex: '-1',
    });
    prev.hidden = true;
    next.hidden = true;
    prev.innerHTML = ARROW_ICON;
    next.innerHTML = ARROW_ICON;

    const message = createElement('p', 'card-size-bar__message', { role: 'alert' });
    message.hidden = true;

    bar.append(prev, list, next, message);
    // `hidden` is dropped once built (visibility is handled by CSS) so overflow can be measured.
    bar.hidden = false;
    requestAnimationFrame(() => updateArrows(bar));
  }

  function init(card) {
    if (card.dataset.cardQuickAddReady) return;
    const state = getState(card);
    if (!state) return;

    try {
      render(card, activeProductId(card, state));
      card.dataset.cardQuickAddReady = 'true';
    } catch (error) {
      console.error('card-quick-add: render failed', error);
    }
  }

  function scan(root = document) {
    root.querySelectorAll(SELECTOR.data).forEach((script) => {
      const card = script.closest(SELECTOR.card);
      if (card) init(card);
    });
  }

  // The card's title link is rewritten by the color swatches of every card type
  // (Dynamic-product-card.js and global.js). Use it as the source of truth for the active color.
  // Compared by handle: inside a collection the same product can be `/collections/x/products/h`.
  function handleOf(url) {
    const match = String(url || '').match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function syncActiveColor(card) {
    const state = getState(card);
    if (!state) return;

    const handle = handleOf(card.querySelector('.card__heading a')?.getAttribute('href'));
    const item = handle && state.items.find((entry) => handleOf(entry.url) === handle);
    if (!item) return;

    const opener = card.querySelector(SELECTOR.opener);
    if (opener) opener.dataset.productUrl = item.url;

    const bar = card.querySelector(SELECTOR.bar);
    if (bar && Number(bar.dataset.productId) !== item.id) render(card, item.id);
  }

  // --- Add to cart ------------------------------------------------------------

  function showMessage(bar, text) {
    const message = bar.querySelector('.card-size-bar__message');
    if (!message) return;
    message.textContent = text;
    message.hidden = false;
    clearTimeout(bar._messageTimer);
    bar._messageTimer = setTimeout(() => {
      message.hidden = true;
    }, MESSAGE_TIMEOUT_MS);
  }

  async function addToCart(button, bar) {
    if (bar.classList.contains('is-loading')) return;

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const variantId = button.dataset.variantId;

    bar.classList.add('is-loading');
    button.classList.add('is-loading');
    bar.setAttribute('aria-busy', 'true');
    // Same spinner markup as snippets/loading-spinner.liquid (animation/stroke come from base.css)
    const spinner = createElement('span', 'card-size-bar__spinner', { 'aria-hidden': 'true' });
    spinner.innerHTML =
      '<svg class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg" focusable="false">' +
      '<circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg>';
    button.appendChild(spinner);

    try {
      const body = new FormData();
      body.append('id', variantId);
      body.append('quantity', 1);
      if (cart) {
        body.append('sections', cart.getSectionsToRender().map((section) => section.id));
        body.append('sections_url', window.location.pathname);
        cart.setActiveElement(document.activeElement);
      }

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = body;

      const response = await fetch(`${routes.cart_add_url}`, config);
      const result = await response.json();

      if (result.status) {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'card-size-bar',
          productVariantId: variantId,
          errors: result.errors || result.description,
          message: result.message,
        });
        // Out of stock / over the limit: lock this size and tell the customer.
        if (result.status === 422) {
          button.disabled = true;
          button.classList.add('is-sold-out');
        }
        showMessage(bar, result.description || result.message || '');
        return;
      }

      if (!cart) {
        window.location = window.routes.cart_url;
        return;
      }

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'card-size-bar',
        productVariantId: variantId,
        cartData: result,
      });
      cart.renderContents(result);
      window.dispatchEvent(new CustomEvent('abra:render'));
    } catch (error) {
      console.error(error);
      showMessage(bar, bar.dataset.errorLabel || '');
    } finally {
      spinner.remove();
      bar.classList.remove('is-loading');
      button.classList.remove('is-loading');
      bar.removeAttribute('aria-busy');
    }
  }

  // --- Events (delegated: cards are injected after load by Nosto / section API) --

  // Build as soon as a card is in the DOM (cards are injected later by Nosto / the section API).
  let scanQueued = false;
  function queueScan() {
    if (scanQueued) return;
    scanQueued = true;
    requestAnimationFrame(() => {
      scanQueued = false;
      scan();
    });
  }

  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches?.(SELECTOR.data) || node.querySelector?.(SELECTOR.data)) {
          queueScan();
          return;
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  scan();

  // Safety net: if a card was missed (or its first build failed), build it on first interaction.
  ['mouseover', 'focusin', 'touchstart'].forEach((type) => {
    document.addEventListener(
      type,
      (event) => {
        const card = event.target.closest?.(SELECTOR.card);
        if (card) init(card);
      },
      { passive: true },
    );
  });

  // Hover state is tracked on the whole card (class on the card) in addition to CSS :hover.
  // The title link overlay sits on top of the image and is not inside .card__inner, so
  // checking .card__inner under the pointer is not reliable.
  document.addEventListener(
    'mouseover',
    (event) => {
      const card = event.target.closest?.(SELECTOR.card);
      if (!card || card.classList.contains('is-size-bar-active')) return;
      card.classList.add('is-size-bar-active');
      syncActiveColor(card);
      // The bar is only laid out on desktop-hover: measure overflow (arrows) when it shows.
      const bar = card.querySelector(SELECTOR.bar);
      if (bar) requestAnimationFrame(() => updateArrows(bar));
    },
    { passive: true },
  );
  document.addEventListener(
    'mouseout',
    (event) => {
      const card = event.target.closest?.(SELECTOR.card);
      if (!card || card.contains(event.relatedTarget)) return;
      card.classList.remove('is-size-bar-active');
    },
    { passive: true },
  );

  // Capture phase: runs before <modal-opener> reads data-product-url from the button.
  document.addEventListener(
    'click',
    (event) => {
      const opener = event.target.closest?.(SELECTOR.opener);
      if (!opener) return;
      const card = opener.closest(SELECTOR.card);
      if (card) syncActiveColor(card);
    },
    true,
  );

  document.addEventListener('click', (event) => {
    const arrow = event.target.closest('.card-size-bar__arrow');
    if (arrow) {
      event.preventDefault();
      const bar = arrow.closest(SELECTOR.bar);
      const list = bar.querySelector('.card-size-bar__list');
      const direction = arrow.classList.contains('card-size-bar__arrow--prev') ? -1 : 1;
      list.scrollBy({ left: direction * list.clientWidth * SCROLL_STEP, behavior: 'smooth' });
      return;
    }

    const choose = event.target.closest('.card-size-bar__choose');
    if (choose) {
      event.preventDefault();
      choose.closest(SELECTOR.card)?.querySelector(SELECTOR.opener)?.click();
      return;
    }

    const size = event.target.closest('.card-size-bar__size');
    if (size && !size.disabled) {
      event.preventDefault();
      addToCart(size, size.closest(SELECTOR.bar));
    }
  });

  document.addEventListener(
    'scroll',
    (event) => {
      if (event.target.classList?.contains('card-size-bar__list')) {
        updateArrows(event.target.closest(SELECTOR.bar));
      }
    },
    true,
  );

  // A color swatch changed: point Quick View + size bar at the new color.
  document.addEventListener('card:colorchange', (event) => {
    const card = event.target.closest?.(SELECTOR.card);
    if (!card) return;
    const { productId, productUrl } = event.detail || {};

    const opener = card.querySelector(SELECTOR.opener);
    if (opener && productUrl) opener.dataset.productUrl = productUrl;

    if (getState(card)) {
      render(card, Number(productId));
      card.dataset.cardQuickAddReady = 'true';
    }
  });

  window.CardQuickAdd = { init };
})();
